<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue'
import { toast } from '~/utils/sonner'

interface CurrentSource {
  id: string
  label: string
  ok: boolean
  activationVersion: string | null
  artifactVersion: string | null
  uiBundleMd5: string | null
  uiURL: string | null
  error: string | null
}

interface CurrentRelease {
  consistent: boolean
  activationVersion: string | null
  artifactVersion: string | null
  uiBundleMd5: string | null
  sources: CurrentSource[]
}

interface ReleaseRecord {
  version: string
  createdAt: string | null
  uiBundleMd5: string | null
  asarSize: number | null
  available: boolean
  error: string | null
}

interface RollbackStep {
  id: string
  label: string
  success: boolean
  error: string | null
}

interface RollbackResult {
  success: boolean
  version: string
  activationVersion: string
  previousVersion: string | null
  previousActivationVersion: string | null
  targets: RollbackStep[]
  dns: RollbackStep
}

interface ReleaseListResponse {
  success: true
  current: CurrentRelease
  releases: ReleaseRecord[]
}

const loading = ref(true)
const rollingBackVersion = ref<string | null>(null)
const current = ref<CurrentRelease | null>(null)
const releases = ref<ReleaseRecord[]>([])
const lastOperation = ref<RollbackResult | null>(null)
const loadError = ref<string | null>(null)

const currentStatusLabel = computed(() => {
  if (!current.value) return '状态不可用'
  return current.value.consistent ? '全部入口一致' : '入口状态不一致'
})

const operationSteps = computed(() =>
  lastOperation.value
    ? [...lastOperation.value.targets, lastOperation.value.dns]
    : [],
)

const isCurrent = (release: ReleaseRecord) =>
  release.version === current.value?.artifactVersion

const formatDate = (value: string | null) => {
  if (!value) return '-'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString()
}

const formatBytes = (value: number | null) => {
  if (!Number.isFinite(value) || !value) return '-'
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`
  return `${(value / 1024 / 1024).toFixed(2)} MB`
}

const shortHash = (value: string | null) =>
  value ? `${value.slice(0, 18)}${value.length > 18 ? '…' : ''}` : '-'

const errorText = (error: any) =>
  error?.data?.error || error?.data?.message || error?.message || String(error)

const extractResult = (value: any): RollbackResult | null =>
  value?.result || value?.data?.result || null

const parseReleaseListResponse = (value: unknown): ReleaseListResponse => {
  if (
    !value ||
    typeof value !== 'object' ||
    (value as any).success !== true ||
    !Array.isArray((value as any).current?.sources) ||
    typeof (value as any).current?.consistent !== 'boolean' ||
    !Array.isArray((value as any).releases)
  ) {
    throw new Error('PandaUI 版本 API 返回了无效响应（应为 JSON 版本数据）')
  }
  return value as ReleaseListResponse
}

async function refresh() {
  loading.value = true
  loadError.value = null
  try {
    const response = parseReleaseListResponse(
      await useAdmin.listPandaUiReleases({ limit: 60 }),
    )
    current.value = response.current
    releases.value = response.releases
  } catch (error) {
    current.value = null
    releases.value = []
    loadError.value = errorText(error)
    toast({
      title: 'PandaUI 版本读取失败',
      description: loadError.value,
      variant: 'destructive',
    })
  } finally {
    loading.value = false
  }
}

async function rollback(release: ReleaseRecord) {
  const confirmed = await useDialog().confirm({
    title: isCurrent(release) ? '重新应用当前版本' : '回滚 PandaUI',
    body: [
      `当前实际 UI 版本：${current.value?.artifactVersion || '未知'}`,
      `目标 UI 版本：${release.version}`,
      '',
      '系统会先检查四个存储入口，然后覆盖四份根 sitemeta 和 DNS TXT。',
      '老目录缺少 sitemeta 时会自动补齐；个别入口失败时可再次执行。',
    ].join('\n'),
    confirmButtonText: isCurrent(release) ? '重新应用' : '确认回滚',
    cancelButtonText: '取消',
    confirmButtonVariant: isCurrent(release) ? 'warning' : 'destructive',
  })
  if (confirmed !== 'confirm') return

  rollingBackVersion.value = release.version
  lastOperation.value = null
  try {
    const response = await useAdmin.rollbackPandaUiRelease(release.version, {
      expectedCurrentActivationVersion:
        current.value?.activationVersion || null,
    })
    lastOperation.value = extractResult(response)
    toast({
      title: 'PandaUI sitemeta 已更新',
      description: `当前指向 ${release.version}`,
    })
  } catch (error) {
    lastOperation.value = extractResult(error)
    toast({
      title: 'PandaUI 回滚未完全成功',
      description: errorText(error),
      variant: 'destructive',
    })
  } finally {
    rollingBackVersion.value = null
    await refresh()
  }
}

onMounted(refresh)
</script>

<template lang="pug">
section(class="mx-auto w-full max-w-[1440px] space-y-5 py-4")
  header(class="flex flex-col gap-3 border-b border-border pb-5 sm:flex-row sm:items-end sm:justify-between")
    div
      p(class="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground") Release control
      h1(class="mt-1 text-2xl font-semibold tracking-tight text-foreground") PandaUI 版本
      p(class="mt-1 max-w-2xl text-sm text-muted-foreground") 选择一个已上传的目录，把线上 sitemeta 重新指向该版本。
    Button(variant="outline" size="sm" :disabled="loading || Boolean(rollingBackVersion)" @click="refresh")
      Icon(name="material-symbols:refresh" class="mr-2 h-4 w-4")
      | 刷新状态

  div(v-if="loading && !current" class="grid gap-3 md:grid-cols-2")
    Skeleton(class="h-44 w-full")
    Skeleton(class="h-44 w-full")

  Alert(v-else-if="loadError" variant="destructive")
    AlertTitle PandaUI 版本状态读取失败
    AlertDescription {{ loadError }}

  div(v-else class="space-y-5")
    div(class="grid gap-x-3 gap-y-5 md:grid-cols-[minmax(0,1.35fr)_minmax(280px,0.65fr)]")
      Card(class="gap-0 overflow-hidden border-border py-5 shadow-none")
        CardHeader(class="border-b border-border bg-muted/30 pb-4")
          div(class="flex flex-wrap items-start justify-between gap-3")
            div
              CardDescription 实际使用的 UI 版本
              CardTitle(class="mt-1 font-mono text-xl") {{ current?.artifactVersion || '未知' }}
            Badge(:variant="current?.consistent ? 'default' : 'destructive'") {{ currentStatusLabel }}
        CardContent(class="space-y-4 pt-5")
          div(class="grid gap-3 sm:grid-cols-2")
            div
              p(class="text-xs text-muted-foreground") 发布版本（deployedVersion）
              p(class="mt-1 font-mono text-sm text-foreground") {{ current?.activationVersion || '-' }}
            div
              p(class="text-xs text-muted-foreground") Bundle MD5
              p(class="mt-1 font-mono text-sm text-foreground" :title="current?.uiBundleMd5 || ''") {{ shortHash(current?.uiBundleMd5 || null) }}
          div(class="grid gap-2 sm:grid-cols-2 xl:grid-cols-5")
            div(
              v-for="source in current?.sources || []"
              :key="source.id"
              class="min-w-0 rounded-md border px-3 py-2"
              :class="source.ok ? 'border-border bg-background' : 'border-destructive/40 bg-destructive/5'"
              :title="source.error || source.uiURL || ''"
            )
              div(class="flex items-center gap-2")
                span(class="h-2 w-2 shrink-0 rounded-full" :class="source.ok ? 'bg-emerald-500' : 'bg-destructive'")
                span(class="truncate text-xs font-medium") {{ source.label }}
              p(class="mt-1 truncate font-mono text-[11px] text-muted-foreground") {{ source.artifactVersion || source.error || '-' }}

      Card(class="gap-0 border-border py-5 shadow-none")
        CardHeader(class="pb-3")
          CardTitle(class="text-base") 切换方式
          CardDescription 只覆盖 metadata，不移动或重传 ASAR。
        CardContent(class="space-y-3")
          p(class="text-xs leading-5 text-muted-foreground") 客户端 error 页面检测到 sitemeta 的 MD5 变化后会下载目标 ASAR，最终仍需用户重启客户端。
          div(v-if="lastOperation" class="space-y-2 border-t border-border pt-3")
            div(class="flex items-center justify-between gap-3 text-sm")
              span(class="font-medium") 最近操作
              Badge(:variant="lastOperation.success ? 'default' : 'destructive'") {{ lastOperation.success ? '成功' : '部分失败' }}
            p(class="font-mono text-xs text-muted-foreground") {{ lastOperation.previousVersion || '-' }} → {{ lastOperation.version }}
            p(class="font-mono text-[11px] text-muted-foreground") deployedVersion {{ lastOperation.activationVersion }}
            div(class="grid gap-1.5")
              div(v-for="step in operationSteps" :key="step.id" class="flex items-center justify-between gap-3 text-xs")
                span(class="text-muted-foreground") {{ step.label }}
                span(:class="step.success ? 'text-emerald-600 dark:text-emerald-400' : 'text-destructive'" :title="step.error || ''") {{ step.success ? '完成' : step.error || '失败' }}

    Card(class="gap-0 border-border py-5 shadow-none")
      CardHeader(class="flex flex-row items-start justify-between gap-4 border-b border-border pb-4")
        div
          CardTitle(class="text-base") 可用版本
          CardDescription(class="mt-1") 来自 R2 版本目录；回滚前会校验四个入口的页面、ASAR 和压缩包。
        Badge(variant="outline") {{ releases.length }} 个版本
      CardContent(class="p-0")
        div(class="overflow-x-auto")
          Table
            TableHeader
              TableRow(class="hover:bg-transparent")
                TableHead(class="min-w-[190px]") 版本
                TableHead(class="min-w-[170px]") 上传时间
                TableHead(class="min-w-[110px]") ASAR
                TableHead(class="min-w-[190px]") MD5
                TableHead(class="min-w-[140px] text-right") 操作
            TableBody
              TableRow(v-if="!releases.length")
                TableCell(colspan="5" class="h-28 text-center text-muted-foreground") 没有找到 PandaUI 版本目录。
              TableRow(
                v-for="release in releases"
                :key="release.version"
                :class="isCurrent(release) ? 'bg-muted/35' : ''"
              )
                TableCell
                  div(class="flex items-center gap-2")
                    span(class="font-mono text-sm font-semibold") {{ release.version }}
                    Badge(v-if="isCurrent(release)" variant="default") 当前
                  p(v-if="release.error" class="mt-1 text-xs text-destructive") {{ release.error }}
                TableCell(class="text-sm text-muted-foreground") {{ formatDate(release.createdAt) }}
                TableCell(class="font-mono text-xs") {{ formatBytes(release.asarSize) }}
                TableCell(class="font-mono text-xs" :title="release.uiBundleMd5 || ''") {{ shortHash(release.uiBundleMd5) }}
                TableCell(class="text-right")
                  Button(
                    size="sm"
                    :variant="isCurrent(release) ? 'outline' : 'default'"
                    :disabled="!release.available || Boolean(rollingBackVersion)"
                    @click="rollback(release)"
                  )
                    Spinner(v-if="rollingBackVersion === release.version" class="mr-2")
                    span {{ isCurrent(release) ? '重新应用' : '回滚到此版本' }}
</template>
